import { memo, useState, useCallback } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { Column } from '@veodiagram/shared';

// ============= Entity Node Data Interface =============

export interface EntityNodeData {
  label: string;
  tableName?: string;
  columns: Column[];
  primaryKey: string[];
  description?: string;
  sourceType?: string;
  /** Progressive disclosure level */
  viewLevel?: 'macro' | 'mid' | 'micro';
  /** Whether this node matches current search */
  isSearchHighlight?: boolean;
  /** Whether this node is focused or a direct neighbour of focused node */
  isFocused?: boolean;
  /** Opacity multiplier for neighbourhood dimming (0–1) */
  focusOpacity?: number;
  /** User annotation alias */
  alias?: string;
  /** User annotation description */
  annotationDescription?: string;
  /** Whether annotation editing is enabled globally */
  showAnnotations?: boolean;
  /** Callback when annotation changes */
  onAnnotationChange?: (entityId: string, alias: string, description: string) => void;
  /** Node ID for annotation callbacks */
  nodeId?: string;
  [key: string]: unknown;
}

// ============= Icons =============

const KeyIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="currentColor">
    <path d="M5.5 0a5.5 5.5 0 0 1 4.383 8.823l5.417 5.417a.75.75 0 0 1-1.06 1.06l-5.417-5.417A5.5 5.5 0 1 1 5.5 0zm0 1.5a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"/>
  </svg>
);

const ForeignKeyIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="currentColor">
    <path d="M14 1H2a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z"/>
    <path d="M6.5 5.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1H7.5v2a.5.5 0 0 1-1 0v-2.5z"/>
    <path d="M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
  </svg>
);

// ============= Column Type Badge =============

function getTypeBadgeColor(type: string): string {
  switch (type) {
    case 'uuid':
    case 'string':
    case 'text':
      return 'bg-blue-100 text-blue-700';
    case 'int':
    case 'bigint':
    case 'float':
    case 'decimal':
      return 'bg-green-100 text-green-700';
    case 'boolean':
      return 'bg-purple-100 text-purple-700';
    case 'date':
    case 'datetime':
    case 'timestamp':
      return 'bg-orange-100 text-orange-700';
    case 'json':
    case 'jsonb':
      return 'bg-yellow-100 text-yellow-700';
    case 'enum':
      return 'bg-pink-100 text-pink-700';
    case 'array':
      return 'bg-cyan-100 text-cyan-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

// ============= Column Row Component =============

interface ColumnRowProps {
  column: Column;
  isLast: boolean;
}

function ColumnRow({ column, isLast }: ColumnRowProps) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 text-xs ${
        !isLast ? 'border-b border-slate-200' : ''
      }`}
    >
      {/* Key indicators */}
      <div className="flex items-center gap-0.5 w-8">
        {column.isPrimaryKey && (
          <KeyIcon className="w-3 h-3 text-amber-500" />
        )}
        {column.isForeignKey && (
          <ForeignKeyIcon className="w-3 h-3 text-indigo-500" />
        )}
      </div>

      {/* Column name */}
      <span className={`flex-1 font-medium truncate ${
        column.isPrimaryKey ? 'text-amber-700' :
        column.isForeignKey ? 'text-indigo-700' :
        'text-slate-700'
      }`}>
        {column.name}
        {!column.isNullable && <span className="text-red-500 ml-0.5">*</span>}
      </span>

      {/* Type badge */}
      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getTypeBadgeColor(column.type)}`}>
        {column.rawType || column.type}
      </span>
    </div>
  );
}

// ============= Annotation Editor =============

interface AnnotationEditorProps {
  alias: string;
  description: string;
  onSave: (alias: string, description: string) => void;
  onCancel: () => void;
}

function AnnotationEditor({ alias, description, onSave, onCancel }: AnnotationEditorProps) {
  const [editAlias, setEditAlias] = useState(alias);
  const [editDesc, setEditDesc] = useState(description);

  return (
    <div className="px-3 py-2 bg-yellow-50 border-t border-yellow-200 space-y-1.5">
      <input
        type="text"
        value={editAlias}
        onChange={e => setEditAlias(e.target.value)}
        placeholder="Alias (e.g. Users)"
        className="w-full px-2 py-1 text-xs border border-yellow-300 rounded bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-yellow-400"
        autoFocus
      />
      <textarea
        value={editDesc}
        onChange={e => setEditDesc(e.target.value)}
        placeholder="Description..."
        rows={2}
        className="w-full px-2 py-1 text-xs border border-yellow-300 rounded bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-yellow-400 resize-none"
      />
      <div className="flex gap-1.5">
        <button
          onClick={() => onSave(editAlias, editDesc)}
          className="flex-1 px-2 py-1 text-xs bg-yellow-400 text-yellow-900 rounded hover:bg-yellow-500 font-medium"
        >
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 px-2 py-1 text-xs bg-white text-slate-600 rounded hover:bg-slate-100 border border-slate-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============= Entity Node Component =============

export const EntityNode = memo(({ data, selected }: NodeProps) => {
  const entityData = data as EntityNodeData;
  const [isEditingAnnotation, setIsEditingAnnotation] = useState(false);

  const viewLevel = entityData.viewLevel || 'micro';
  const isSearchHighlight = entityData.isSearchHighlight || false;
  const focusOpacity = entityData.focusOpacity ?? 1;
  const showAnnotations = entityData.showAnnotations !== false;
  const alias = entityData.alias || '';
  const annotationDescription = entityData.annotationDescription || '';

  const columns = entityData.columns || [];
  const maxVisibleColumns = 8;

  // Determine which columns to show based on viewLevel
  let visibleColumns: Column[];
  let hiddenCount = 0;
  if (viewLevel === 'macro') {
    visibleColumns = [];
  } else if (viewLevel === 'mid') {
    visibleColumns = columns.filter(c => c.isPrimaryKey || c.isForeignKey);
  } else {
    visibleColumns = columns.slice(0, maxVisibleColumns);
    hiddenCount = columns.length - maxVisibleColumns;
  }

  const handleHeaderDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (showAnnotations) {
      setIsEditingAnnotation(true);
    }
  }, [showAnnotations]);

  const handleAnnotationSave = useCallback((newAlias: string, newDesc: string) => {
    setIsEditingAnnotation(false);
    if (entityData.onAnnotationChange && entityData.nodeId) {
      entityData.onAnnotationChange(entityData.nodeId, newAlias, newDesc);
    }
  }, [entityData]);

  const displayName = (showAnnotations && alias) ? `${alias} (${entityData.label})` : entityData.label;

  return (
    <div
      style={{ opacity: focusOpacity }}
      className={`
        bg-white rounded-lg shadow-lg border-2 overflow-hidden
        min-w-[180px] max-w-[280px]
        transition-all duration-200
        ${selected ? 'border-blue-500 ring-2 ring-blue-200' :
          isSearchHighlight ? 'border-yellow-400 ring-2 ring-yellow-200' :
          'border-slate-300'}
      `}
    >
      {/* Handles — all 4 sides for flexible edge routing */}
      <Handle type="target" position={Position.Left}   style={{ opacity: 0 }} id="left" />
      <Handle type="target" position={Position.Right}  style={{ opacity: 0 }} id="right" />
      <Handle type="target" position={Position.Top}    style={{ opacity: 0 }} id="top" />
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} id="bottom" />
      <Handle type="source" position={Position.Left}   style={{ opacity: 0 }} id="left-src" />
      <Handle type="source" position={Position.Right}  style={{ opacity: 0 }} id="right-src" />
      <Handle type="source" position={Position.Top}    style={{ opacity: 0 }} id="top-src" />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} id="bottom-src" />

      {/* Header */}
      <div
        className="bg-indigo-600 px-3 py-2 cursor-pointer select-none group"
        onDoubleClick={handleHeaderDoubleClick}
        title={showAnnotations ? 'Double-click to add annotation' : undefined}
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-indigo-200 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
          <span className="text-white font-semibold text-sm truncate flex-1">
            {displayName}
          </span>
          {showAnnotations && (
            <svg className="w-3 h-3 text-indigo-300 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          )}
        </div>
        {entityData.tableName && entityData.tableName !== entityData.label && (
          <div className="text-indigo-200 text-[10px] mt-0.5 truncate">
            Table: {entityData.tableName}
          </div>
        )}
        {showAnnotations && annotationDescription && (
          <div className="text-indigo-200 text-[10px] mt-0.5 truncate" title={annotationDescription}>
            {annotationDescription}
          </div>
        )}
        {viewLevel === 'macro' && (
          <div className="text-indigo-200 text-[10px] mt-0.5">
            {columns.length} columns
          </div>
        )}
      </div>

      {/* Columns — hidden in macro mode */}
      {viewLevel !== 'macro' && (
        <div className="divide-y divide-slate-100">
          {visibleColumns.map((column, index) => (
            <ColumnRow
              key={column.name}
              column={column}
              isLast={index === visibleColumns.length - 1 && hiddenCount <= 0}
            />
          ))}

          {/* Hidden columns indicator (micro mode only) */}
          {hiddenCount > 0 && (
            <div className="px-3 py-1.5 text-xs text-slate-400 text-center bg-slate-50">
              +{hiddenCount} more columns
            </div>
          )}

          {/* Empty state for mid mode with no PK/FK */}
          {viewLevel === 'mid' && visibleColumns.length === 0 && (
            <div className="px-3 py-1.5 text-xs text-slate-400 text-center bg-slate-50">
              No keys
            </div>
          )}
        </div>
      )}

      {/* Annotation Editor */}
      {isEditingAnnotation && (
        <AnnotationEditor
          alias={alias}
          description={annotationDescription}
          onSave={handleAnnotationSave}
          onCancel={() => setIsEditingAnnotation(false)}
        />
      )}

      {/* Source type badge */}
      {entityData.sourceType && (
        <div className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[9px] font-medium bg-slate-700 text-white rounded-full">
          {entityData.sourceType}
        </div>
      )}
    </div>
  );
});

EntityNode.displayName = 'EntityNode';

// ============= Node Types Registry =============

export const schemaNodeTypes = {
  entity: EntityNode,
  database: EntityNode, // Alias for compatibility
};
